import { FormEvent, useEffect, useState, useMemo, ChangeEvent } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import {

  BsCalendarEvent,
  BsCpu,
  BsLink45Deg,
  BsPersonBadge,
  BsSave,
  BsTag,
} from "react-icons/bs";

import { DTOEquipoInventario } from "../../../DTO/Inventario/DTOEquipoInventario";
import { DTOEstadoEquipo, DTOTipoEquipo } from "../../../DTO/Inventario/DTOCatalogosInventario";
import { DTOGestionarEquipo } from "../../../DTO/Inventario/DTOGestionInventario";

interface DTOEmpleadoCatalogo {
  idEmpleado: number;
  numEmpleado: string;
  name: string;
  activo: number;
}
type TipoAsignacion =
  | "EMPLEADO"
  | "RESPONSABLE"
  | "SIN_ASIGNAR";

interface FormularioEquipoProps {
  show: boolean;
  equipo: DTOEquipoInventario | null;
  tiposEquipo: DTOTipoEquipo[];
  estadosEquipo: DTOEstadoEquipo[];
  empleados: DTOEmpleadoCatalogo[];
  onCerrar: () => void;
  onGuardar: (dto: DTOGestionarEquipo) => Promise<void>;
}

const crearFormularioInicial = (): DTOGestionarEquipo => ({
  accion: "INSERTAR",
  idTipoEquipo: null,
  idEstadoEquipo: null,
  nombreEquipo: "",
  serviceTagSerial: "",
  ligaEspecificaciones: "",
  numEmpleadoAsignado: "",
  nombreResponsable: "",
  observaciones: "",
  localidad: "", 
  fechaMantenimiento: "",
});

const FormularioEquipo = ({
  show,
  equipo,
  tiposEquipo,
  estadosEquipo,
  empleados,
  onCerrar,
  onGuardar,
}: FormularioEquipoProps) => {
  const [guardando, setGuardando] = useState(false);
  const [validado, setValidado] = useState(false);
  const [formulario, setFormulario] = useState<DTOGestionarEquipo>(crearFormularioInicial());
  const [textoEmpleadoSeleccionado, setTextoEmpleadoSeleccionado] = useState("");
  const [tipoAsignacion, setTipoAsignacion] =
  useState<TipoAsignacion>("SIN_ASIGNAR");

const [nombreResponsable, setNombreResponsable] =
  useState("");

  const catalogoTipos = useMemo(() => (Array.isArray(tiposEquipo) ? tiposEquipo : []), [tiposEquipo]);
  const catalogoEstados = useMemo(() => (Array.isArray(estadosEquipo) ? estadosEquipo : []), [estadosEquipo]);

  const listaEmpleadosDisponibles = useMemo(() => {
    const pool = Array.isArray(empleados) ? empleados : [];
    return pool.filter((emp: DTOEmpleadoCatalogo) => Number(emp.activo) === 1);
  }, [empleados]);

 useEffect(() => {
  setValidado(false);

  if (equipo) {
    // Detectar el ID del tipo de equipo.
    let idTipoDetectado = equipo.idTipoEquipo ?? null;

    if (!idTipoDetectado && equipo.tipoEquipo) {
      const coincidenciaTipo = catalogoTipos.find(
        (tipo) =>
          tipo.clave.toUpperCase() ===
          equipo.tipoEquipo?.toUpperCase()
      );

      if (coincidenciaTipo) {
        idTipoDetectado = coincidenciaTipo.idTipoEquipo;
      }
    }

    // Detectar el ID del estado.
    let idEstadoDetectado = equipo.idEstadoEquipo ?? null;

    if (!idEstadoDetectado && equipo.estadoEquipo) {
      const coincidenciaEstado = catalogoEstados.find(
        (estado) =>
          estado.estado.toLowerCase() ===
          equipo.estadoEquipo?.toLowerCase()
      );

      if (coincidenciaEstado) {
        idEstadoDetectado = coincidenciaEstado.idEstadoEquipo;
      }
    }

    // Cargar la información del equipo en el formulario.
    setFormulario({
      accion: "ACTUALIZAR",
      idEquipo: equipo.idEquipo,
      idTipoEquipo: idTipoDetectado,
      idEstadoEquipo: idEstadoDetectado,
      nombreEquipo: equipo.nombreEquipo,
      serviceTagSerial: equipo.serviceTagSerial ?? "",
      ligaEspecificaciones: equipo.ligaEspecificaciones ?? "",
      numEmpleadoAsignado: equipo.numEmpleadoAsignado ?? "",
      nombreResponsable: equipo.nombreResponsable ?? "",
      observaciones: equipo.observaciones ?? "",
      localidad: equipo.localidad ?? "",
      fechaMantenimiento:
    equipo.fechaMantenimiento?.slice(0, 10) ?? ""
    });

    // Determinar el tipo de asignación existente.
    if (equipo.numEmpleadoAsignado) {
      setTipoAsignacion("EMPLEADO");
      setNombreResponsable("");

      const empleadoExistente = listaEmpleadosDisponibles.find(
        (empleado) =>
          empleado.numEmpleado === equipo.numEmpleadoAsignado
      );

      setTextoEmpleadoSeleccionado(
        empleadoExistente
          ? `${empleadoExistente.numEmpleado} - ${empleadoExistente.name}`
          : equipo.numEmpleadoAsignado
      );
    } else if (equipo.nombreResponsable) {
      setTipoAsignacion("RESPONSABLE");
      setNombreResponsable(equipo.nombreResponsable);
      setTextoEmpleadoSeleccionado("");
    } else {
      setTipoAsignacion("SIN_ASIGNAR");
      setTextoEmpleadoSeleccionado("");
      setNombreResponsable("");
    }
  } else {
    // Reiniciar el formulario para registrar un equipo nuevo.
    setFormulario(crearFormularioInicial());
    setTipoAsignacion("SIN_ASIGNAR");
    setTextoEmpleadoSeleccionado("");
    setNombreResponsable("");
  }
}, [
  equipo,
  show,
  catalogoTipos,
  catalogoEstados,
  listaEmpleadosDisponibles,
]);




  const manejarCambioEmpleado = (e: ChangeEvent<HTMLInputElement>) => {
    const valorInput = e.target.value;
    setTextoEmpleadoSeleccionado(valorInput);

    const empEncontrado = listaEmpleadosDisponibles.find(
      (emp: DTOEmpleadoCatalogo) => `${emp.numEmpleado} - ${emp.name}` === valorInput
    );

    if (empEncontrado) {
      setFormulario(prev => ({ ...prev, numEmpleadoAsignado: empEncontrado.numEmpleado }));
    } else if (valorInput.trim() === "") {
      setFormulario(prev => ({ ...prev, numEmpleadoAsignado: "" }));
    }
  };

const manejarCambioTipoAsignacion = (
  nuevoTipo: TipoAsignacion
) => {
  setTipoAsignacion(nuevoTipo);

  if (nuevoTipo === "EMPLEADO") {
    setNombreResponsable("");

    setFormulario((prev) => ({
      ...prev,
      nombreResponsable: "",
    }));

    return;
  }

  if (nuevoTipo === "RESPONSABLE") {
    setTextoEmpleadoSeleccionado("");

    setFormulario((prev) => ({
      ...prev,
      numEmpleadoAsignado: "",
    }));

    return;
  }

  setTextoEmpleadoSeleccionado("");
  setNombreResponsable("");

  setFormulario((prev) => ({
    ...prev,
    numEmpleadoAsignado: "",
    nombreResponsable: "",
  }));
};





  const enviarFormulario = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (
      form.checkValidity() === false ||
      !formulario.idTipoEquipo ||
      !formulario.idEstadoEquipo ||
      !formulario.nombreEquipo?.trim()
    ) {
      event.stopPropagation();
      setValidado(true);
      return;
    }

    setGuardando(true);
    try {
      
      await onGuardar({
  ...formulario,
  nombreEquipo: formulario.nombreEquipo.trim(),
  serviceTagSerial:
    formulario.serviceTagSerial?.trim() || null,
  ligaEspecificaciones:
    formulario.ligaEspecificaciones?.trim() || null,
  numEmpleadoAsignado:
    tipoAsignacion === "EMPLEADO"
      ? formulario.numEmpleadoAsignado?.trim() || null
      : null,
  nombreResponsable:
    tipoAsignacion === "RESPONSABLE"
      ? nombreResponsable.trim() || null
      : null,
  observaciones:
    formulario.observaciones?.trim() || null,
  localidad:
    formulario.localidad?.trim() || null,
    fechaMantenimiento:
  formulario.fechaMantenimiento || null,
});
    } catch (err) {
      throw err;
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onCerrar} centered size="lg" backdrop="static">
      <Form noValidate validated={validado} onSubmit={enviarFormulario}>
        <Modal.Header closeButton className="border-bottom bg-white">
          <Modal.Title className="d-flex align-items-center gap-3 fs-5">
            <div className="d-flex align-items-center justify-content-center rounded-3" style={{ width: 42, height: 42, color: "#2563eb", backgroundColor: "#eff6ff" }}>
              <BsCpu size={21} />
            </div>
            <div>
              <div className="fw-bold">{equipo ? "Editar equipo" : "Registrar equipo"}</div>
              <div className="text-muted fw-normal" style={{ fontSize: 12 }}>Información general del activo</div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: "#f8fafc" }}>
          {/* INFORMACIÓN DEL ACTIVO */}
          <Card className="border-0 shadow-sm mb-3" style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <Card.Body>
              <div className="fw-bold mb-3 pb-2 border-bottom" style={{ color: "#0f172a" }}>Información del equipo</div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Tipo de equipo <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={formulario.idTipoEquipo ?? ""}
                      onChange={(e) => setFormulario({ ...formulario, idTipoEquipo: Number(e.target.value) || null })}
                      required
                    >
                      <option value="">Seleccione un tipo</option>
                      {catalogoTipos.map((tipo) => (
                        <option key={tipo.idTipoEquipo} value={tipo.idTipoEquipo}>{tipo.clave} - {tipo.descripcion}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">Seleccione un tipo de equipo.</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Estado <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={formulario.idEstadoEquipo ?? ""}
                      onChange={(e) => setFormulario({ ...formulario, idEstadoEquipo: Number(e.target.value) || null })}
                      required
                    >
                      <option value="">Seleccione un estado</option>
                      {catalogoEstados.map((estado) => (
                        <option key={estado.idEstadoEquipo} value={estado.idEstadoEquipo}>{estado.estado}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">Seleccione un estado.</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Nombre del equipo <span className="text-danger">*</span></Form.Label>
                    <div className="position-relative">
                      <BsCpu className="position-absolute text-muted" style={{ left: 12, top: 12, zIndex: 2 }} />
                      <Form.Control
                        type="text"
                        className="ps-5"
                        value={formulario.nombreEquipo ?? ""}
                        onChange={(e) => setFormulario({ ...formulario, nombreEquipo: e.target.value })}
                        placeholder="Ej. PC-RH-01"
                        maxLength={100}
                        required
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Service Tag / Serial</Form.Label>
                    <div className="position-relative">
                      <BsTag className="position-absolute text-muted" style={{ left: 12, top: 12, zIndex: 2 }} />
                      <Form.Control
                        type="text"
                        className="ps-5"
                        value={formulario.serviceTagSerial ?? ""}
                        onChange={(e) => setFormulario({ ...formulario, serviceTagSerial: e.target.value })}
                        placeholder="Identificador del fabricante"
                        maxLength={100}
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col md={6}>
  <Form.Group>
    <Form.Label className="small fw-semibold">
      Fecha de mantenimiento
    </Form.Label>

    <div className="position-relative">
      <BsCalendarEvent
        className="position-absolute text-muted"
        style={{
          left: 12,
          top: 12,
          zIndex: 2,
        }}
      />

      <Form.Control
        type="date"
        className="ps-5"
        value={formulario.fechaMantenimiento ?? ""}
        onChange={(event) =>
          setFormulario({
            ...formulario,
            fechaMantenimiento: event.target.value,
          })
        }
      />
    </div>

    <Form.Text
      className="text-muted"
      style={{ fontSize: "11px" }}
    >
      Fecha prevista para el próximo mantenimiento.
    </Form.Text>
  </Form.Group>
</Col>
              </Row>
            </Card.Body>
          </Card>

          {/* ASIGNACIÓN DE EMPLEADO */}
          <Card className="border-0 shadow-sm" style={{ borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <Card.Body>
              <div className="fw-bold mb-3 pb-2 border-bottom" style={{ color: "#0f172a" }}>Asignación y documentación</div>
              <Row className="g-3">

                <Col md={6}>
  <Form.Group>
    <Form.Label className="small fw-semibold">
      Localidad
    </Form.Label>

    <Form.Select
      value={formulario.localidad ?? ""}
      onChange={(e) =>
        setFormulario((prev) => ({
          ...prev,
          localidad: e.target.value,
        }))
      }
    >
      <option value="">Sin localidad asignada</option>
      <option value="Nuevo Laredo">Nuevo Laredo</option>
      <option value="Monterrey">Monterrey</option>
      <option value="Laredo, Texas">Laredo Texas</option>
       <option value="Piedras Negras">Piedras Negras</option>
       <option value="Estado de mexico">Estado de mexico</option>
       
    </Form.Select>

    <Form.Text className="text-muted" style={{ fontSize: "11px" }}>
      Selecciona la sucursal donde se encuentra el equipo.
    </Form.Text>
  </Form.Group>
</Col>
      <Col xs={12}>
  <Form.Group>
    <Form.Label className="small fw-semibold">
      Tipo de asignación
    </Form.Label>

    <div className="d-flex flex-wrap gap-3">
      <Form.Check
        type="radio"
        id="asignacion-empleado"
        name="tipoAsignacion"
        label="Empleado"
        checked={tipoAsignacion === "EMPLEADO"}
        onChange={() =>
          manejarCambioTipoAsignacion("EMPLEADO")
        }
      />

      <Form.Check
        type="radio"
        id="asignacion-responsable"
        name="tipoAsignacion"
        label="Responsable externo"
        checked={tipoAsignacion === "RESPONSABLE"}
        onChange={() =>
          manejarCambioTipoAsignacion("RESPONSABLE")
        }
      />

      <Form.Check
        type="radio"
        id="asignacion-sin-asignar"
        name="tipoAsignacion"
        label="Sin asignar"
        checked={tipoAsignacion === "SIN_ASIGNAR"}
        onChange={() =>
          manejarCambioTipoAsignacion("SIN_ASIGNAR")
        }
      />
    </div>
  </Form.Group>
</Col>

{tipoAsignacion === "EMPLEADO" && (
  <Col md={6}>
    <Form.Group>
      <Form.Label className="small fw-semibold">
        Empleado asignado
      </Form.Label>

      <div className="position-relative">
        <BsPersonBadge
          className="position-absolute text-muted"
          style={{
            left: 12,
            top: 12,
            zIndex: 2,
          }}
        />

        <Form.Control
          type="text"
          list="datalistEmpleados"
          className="ps-5"
          placeholder="Escribe nombre o número de empleado..."
          value={textoEmpleadoSeleccionado}
          onChange={manejarCambioEmpleado}
        />

        <datalist id="datalistEmpleados">
          {listaEmpleadosDisponibles.map(
            (empleado: DTOEmpleadoCatalogo) => (
              <option
                key={empleado.idEmpleado}
                value={`${empleado.numEmpleado} - ${empleado.name}`}
              />
            )
          )}
        </datalist>
      </div>

      <Form.Text
        className="text-muted"
        style={{ fontSize: "11px" }}
      >
        Selecciona un empleado activo del catálogo.
      </Form.Text>
    </Form.Group>
  </Col>
)}

{tipoAsignacion === "RESPONSABLE" && (
  <Col md={6}>
    <Form.Group>
      <Form.Label className="small fw-semibold">
        Nombre del responsable
      </Form.Label>

      <div className="position-relative">
        <BsPersonBadge
          className="position-absolute text-muted"
          style={{
            left: 12,
            top: 12,
            zIndex: 2,
          }}
        />

        <Form.Control
          type="text"
          className="ps-5"
          placeholder="Ej. Patricia Guajardo"
          value={nombreResponsable}
          onChange={(e) => {
            const valor = e.target.value;

            setNombreResponsable(valor);

            setFormulario((prev) => ({
              ...prev,
              nombreResponsable: valor,
            }));
          }}
          maxLength={150}
        />
      </div>

      <Form.Text
        className="text-muted"
        style={{ fontSize: "11px" }}
      >
        Úsalo cuando el equipo esté asignado a una
        persona externa al catálogo de empleados.
      </Form.Text>
    </Form.Group>
  </Col>
)}

{tipoAsignacion === "SIN_ASIGNAR" && (
  <Col md={6}>
    <div
      className="border rounded-3 px-3 py-3 bg-light"
      style={{ minHeight: "74px" }}
    >
      <div className="small fw-semibold">
        Equipo sin asignación
      </div>

      <div
        className="text-muted"
        style={{ fontSize: "11px" }}
      >
        El equipo permanecerá disponible en inventario.
      </div>
    </div>
  </Col>
)}







                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Liga de especificaciones</Form.Label>
                    <div className="position-relative">
                      <BsLink45Deg className="position-absolute text-muted" style={{ left: 12, top: 12, zIndex: 2 }} />
                      <Form.Control
                        type="url"
                        className="ps-5"
                        value={formulario.ligaEspecificaciones ?? ""}
                        onChange={(e) => setFormulario({ ...formulario, ligaEspecificaciones: e.target.value })}
                        placeholder="https://..."
                        maxLength={500}
                      />
                    </div>
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group>
                    <Form.Label className="small fw-semibold">Observaciones</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formulario.observaciones ?? ""}
                      onChange={(e) => setFormulario({ ...formulario, observaciones: e.target.value })}
                      placeholder="Información adicional sobre el equipo..."
                      maxLength={500}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer className="bg-white border-top">
          <Button variant="light" onClick={onCerrar} disabled={guardando}>Cancelar</Button>
          <Button variant="primary" type="submit" disabled={guardando} style={{ background: "#2563eb", border: "none" }}>
            {guardando ? (
              <><Spinner size="sm" animation="border" className="me-2" />Guardando...</>
            ) : (
              <><BsSave className="me-2" />Guardar activo</>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FormularioEquipo;